/**
 * 健康度フィルタヘッダーコンポーネント
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business テーブルヘッダーに健康度フィルタ（健康/要注意/危険/データなし）のドロップダウンを統合
 * @graph-connects none
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** 健康度フィルタヘッダー */
/** @graph-connects none */
export function HealthFilterHeader({
  value,
  onChange,
  onInfoClick,
}: {
  value: string;
  onChange: (v: string) => void;
  onInfoClick: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const opts = [
    { key: '', label: t('projects.health.filter.all') },
    { key: 'healthy', label: t('projects.health.filter.healthy'), color: '#16a34a' },
    { key: 'warning', label: t('projects.health.filter.warning'), color: '#f97316' },
    { key: 'danger', label: t('projects.health.filter.danger'), color: '#ef4444' },
    { key: 'none', label: t('projects.health.filter.noData'), color: '#6b7280' },
  ];
  return (
    <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap relative">
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          className={`inline-flex items-center gap-1 hover:text-[var(--color-text-primary)] transition-colors ${value ? 'text-[var(--color-accent-primary)]' : ''}`}
          onClick={() => {
            setOpen(!open);
          }}
        >
          {value && (
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: opts.find((o) => o.key === value)?.color }}
            />
          )}
          {t('projects.health.title')}
          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
            <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onInfoClick}
          className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[var(--color-text-muted)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:border-[var(--color-text-primary)] transition-colors text-[10px] leading-none"
          aria-label={t('projects.health.title')}
        >
          ?
        </button>
      </span>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
            }}
          />
          <div className="absolute top-full left-2 z-20 mt-1 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-lg shadow-lg py-1 min-w-[140px]">
            {opts.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--color-bg-hover)] flex items-center gap-2 ${value === opt.key ? 'text-[var(--color-accent-primary)] font-medium' : 'text-[var(--color-text-primary)]'}`}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
              >
                {opt.color && (
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />
                )}
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </th>
  );
}
